import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { apiFetch } from '../lib/api.ts';

export interface DispatchPlanLine {
  sku: string;
  qtyOrdered: number;
  unitTags: number;
  cartons: Array<{ pcsPerCarton: number; cartonTags: number }>;
  declaredQty: number | null;
  isExcluded: boolean;
  recommendedCartonSize: number | null;
  plannedPieces: number;
  variance: number;
}

export interface DispatchPlanSummary {
  id: number;
  doNo: string;
  status: 'draft' | 'submitted' | 'cancelled' | 'legacy';
  overPlanned: boolean;
  note: string | null;
  lines: DispatchPlanLine[];
}

const sum = (lines: DispatchPlanLine[], f: (l: DispatchPlanLine) => number): number =>
  lines.reduce((t, l) => t + f(l), 0);

/** Lines the worker has moved away from what we suggested. */
const overridden = (lines: DispatchPlanLine[]): number =>
  lines.filter((l) => {
    if (l.isExcluded) return l.declaredQty !== l.qtyOrdered;
    const size = l.recommendedCartonSize;
    if (size === null || size <= 1 || l.qtyOrdered < size) return l.unitTags !== l.qtyOrdered;
    return (
      l.unitTags !== l.qtyOrdered % size ||
      l.cartons.length !== 1 ||
      l.cartons[0]?.pcsPerCarton !== size ||
      l.cartons[0]?.cartonTags !== Math.floor(l.qtyOrdered / size)
    );
  }).length;

export function DispatchListPage() {
  const qc = useQueryClient();
  // A legacy DO has no plan, so its detail page has nothing to show — the
  // re-open action has to live here, on the row.
  const reopen = useMutation({
    mutationFn: (doNo: string) =>
      apiFetch(`/api/dashboard/dispatch/${encodeURIComponent(doNo)}/reopen`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['dispatch-plans'] }),
  });

  const q = useQuery({
    queryKey: ['dispatch-plans'],
    queryFn: () => apiFetch<DispatchPlanSummary[]>('/api/dashboard/dispatch'),
  });

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  // Derived from the data, so the filter can never offer a status that isn't
  // present — today that is draft, submitted and legacy.
  const statuses = useMemo(
    () => [...new Set((q.data ?? []).map((p) => p.status))].sort(),
    [q.data],
  );

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (q.data ?? []).filter((p) => {
      if (status && p.status !== status) return false;
      if (!needle) return true;
      return p.doNo.toLowerCase().includes(needle);
    });
  }, [q.data, search, status]);

  const filtering = Boolean(search.trim() || status);

  return (
    <>
      <header className="main-header">
        <h1 className="main-title">Dispatch</h1>
        <span className="main-subtitle">
          {q.data
            ? filtering
              ? `${rows.length} of ${q.data.length} open`
              : `${q.data.length} open`
            : ''}
        </span>
      </header>
      {q.data && q.data.length > 0 && (
        <div className="toolbar">
          <input
            placeholder="Search DO no…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {statuses.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {filtering && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setStatus('');
              }}
            >
              Clear
            </button>
          )}
        </div>
      )}
      {q.error && <div className="error">{(q.error as Error).message}</div>}
      {reopen.error && <div className="error">{(reopen.error as Error).message}</div>}
      {q.isLoading && <div className="loading">Loading…</div>}
      {q.data && q.data.length === 0 && (
        <p className="empty">
          Nothing to dispatch. A delivery order appears here once its lines sync from OBM.
        </p>
      )}
      {q.data && q.data.length > 0 && rows.length === 0 && (
        <p className="empty">No dispatch matches that filter.</p>
      )}
      {rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>DO</th>
              <th>Status</th>
              <th className="num">SKUs</th>
              <th className="num">Ordered</th>
              <th className="num">Planned</th>
              <th className="num">Adjusted</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.doNo}>
                <td className="mono">
                  {p.status === 'legacy' ? (
                    p.doNo
                  ) : (
                    <Link to={`/dispatch/${encodeURIComponent(p.doNo)}`}>{p.doNo}</Link>
                  )}
                </td>
                <td>
                  <span
                    className={`badge ${
                      p.status === 'draft'
                        ? 'badge-pending'
                        : p.status === 'legacy'
                          ? 'badge-neutral'
                          : 'badge-ok'
                    }`}
                  >
                    {p.status}
                  </span>
                  {p.overPlanned && <span className="badge badge-danger"> over</span>}
                </td>
                {p.status === 'legacy' ? (
                  <td colSpan={4}>
                    <button
                      type="button"
                      disabled={reopen.isPending}
                      onClick={() => reopen.mutate(p.doNo)}
                    >
                      Re-open for keying
                    </button>{' '}
                    <span className="keying-summary">
                      Pushed before keying existed — cancels the SmartLink order and creates a
                      draft you can key.
                    </span>
                  </td>
                ) : (
                  <>
                    <td className="num">{p.lines.length}</td>
                    <td className="num">{sum(p.lines, (l) => l.qtyOrdered)}</td>
                    <td className="num">{sum(p.lines, (l) => l.plannedPieces)}</td>
                    <td className="num">{overridden(p.lines)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
